import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "bootstrap";
import { useOutletContext, Link } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import AttendanceView from "../personalinfo/attendanceView";
import LeavesView from "../personalinfo/teamLeaveView";
import TravelView from "../personalinfo/teamTravelView";
import FeedbackView from "../personalinfo/addConfirmationFeed";

function EmployeeList() {
  const [userData, setUserData] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const { setGlobalLoading, token } = useOutletContext();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedModule, setSelectedModule] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [confirmationStatus, setConfirmationStatus] = useState({});

  const openEmployeeModule = (employee, module) => {
    setSelectedEmployee(employee);
    setSelectedModule(module);

    setTimeout(() => {
      const modalElement = document.getElementById("employeeModuleModal");

      if (modalElement) {
        const modal = Modal.getOrCreateInstance(modalElement);
        modal.show();
      }
    }, 0);
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setGlobalLoading(true);
        const res = await axios.get(
          `${API_BASE_URL}/employeeinfos/employeelist/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUserData(res?.data?.rows);
        const departments = [
          ...new Set(
            res.data.rows.map((emp) => emp.Department).filter(Boolean)
          ),
        ];
        setDepartmentOptions(departments);
      } catch (err) {
        console.error("Error fetching data:", err);
        setUserData([]);
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const columns = [
    {
      accessorKey: "id",
      header: "Emp_id",
      size: 30,
    },
    {
      accessorKey: "userpic",
      header: "Picture",
      size: 50,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const imageUrl = row.original.userpic
          ? `https://pinepositivemoves.com/files/media/${row.original.userpic}`
          : "https://smarthr.dreamstechnologies.com/html/template/assets/img/users/user-39.jpg";

        return (
          <img
            src={imageUrl}
            alt="Profile"
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-profile.png";
            }}
          />
        );
      },
    },
    {
      accessorKey: "Fullname",
      header: "Name",
      size: 150,
      Cell: ({ row }) => (
        <Link
          to={`/employeeinfo/view/${row.original.id}`}
        >
          {row.original.Fullname || row.original.username}
        </Link>
      ),
    },
    {
      accessorKey: "Designation",
      header: "Designation",
      size: 130,
    },
    {
      accessorKey: "Department",
      header: "Department",
      size: 130,
      filterVariant: "select",
      filterSelectOptions: departmentOptions,
    },

    {
      accessorKey: "Level",
      header: "Level",
      size: 100,
      Cell: ({ cell }) => cell.getValue() || "NA",
    },
    {
      accessorKey: "Worklocation",
      header: "Location",
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 50,
      enableColumnFilter: false,
      enableSorting: false,
      filterVariant: "select",
      filterSelectOptions: ["active", "inactive"],
      Cell: ({ cell }) => (
        <span
          style={{
            backgroundColor: cell.getValue() === "active" ? "green" : "red",
            color: "white",
            padding: "3px 8px",
            borderRadius: "12px",
            fontSize: "0.8rem",
          }}
        >
          {cell.getValue()}
        </span>
      ),
    },
    {
    id: "actions",
    header: "Actions",
    size: 350,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ row }) => {
      const employee = row.original;
      return (
        <div className="d-flex gap-1 flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEmployeeModule(employee, "attendance")}>
            Attendance
          </button>

          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => openEmployeeModule(employee, "leaves")}>
            Leaves
          </button>

          <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => openEmployeeModule(employee, "travel")}>
            Travel
          </button>

        {confirmationStatus[employee.id] && (
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => openEmployeeModule(employee, "contribution")}>
            Contribution
          </button>
        )}
        </div>
      );
    },
  },
  ];

  useEffect(() => {
  if (!userData.length || !token) return;

  const fetchConfirmationStatus = async () => {
      try {
        const results = await Promise.all(
          userData.map(async (employee) => {
            try {
              const res = await axios.get(
                `${API_BASE_URL}/employeeinfos/check_confirmation/${employee.id}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              return {
                id: employee.id,
                conffeed: res.data?.conffeed === true,
              };
            } catch (error) {
              console.error(
                `Error checking confirmation for employee ${employee.id}:`,
                error
              );

              return {
                id: employee.id,
                conffeed: false,
              };
            }
          })
        );

        const statusMap = {};

        results.forEach((item) => {
          statusMap[item.id] = item.conffeed;
        });

        setConfirmationStatus(statusMap);
      } catch (error) {
        console.error("Error fetching confirmation statuses:", error);
      }
    };

    fetchConfirmationStatus();
  }, [userData, token, API_BASE_URL]);

  return (
    <div className="content">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
          <h5>Employees</h5>
        </div>

        <div className="card-body">
          {userData.length === 0 ? (
            <p className="text-muted">No employees found.</p>
          ) : (
            <MaterialReactTable
              columns={columns}
              data={userData}
              // enableColumnOrdering
              enableFilters // enables filtering UI
              initialState={{
                pagination: { pageIndex : 0, pageSize: 50 },
                columnFilters: [{ id: "status", value: "active" }], // default to active employees
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "8px 10px",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                },
              }}
            />
          )}

        <div className="modal fade" id="employeeModuleModal" tabIndex="-1" aria-labelledby="employeeModuleModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title" id="employeeModuleModalLabel">
                    {selectedModule === "attendance" && "Attendance"}
                    {selectedModule === "leaves" && "Leaves"}
                    {selectedModule === "travel" && "Travel"}
                    {selectedModule === "contribution" && "Contribution"}
                  </h5>

                  {selectedEmployee && (
                    <small className="text-muted">
                      {selectedEmployee.Fullname || selectedEmployee.username}
                      {" "}
                      ({selectedEmployee.id})
                    </small>
                  )}
                </div>

                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                {selectedEmployee && selectedModule === "attendance" && (
                  <AttendanceView
                    viewUserId={selectedEmployee.id}
                  />
                )}

                {selectedEmployee && selectedModule === "leaves" && (
                  <LeavesView
                    viewUserId={selectedEmployee.id}
                  />
                )}

                {selectedEmployee && selectedModule === "travel" && (
                  <TravelView
                    viewUserId={selectedEmployee.id}
                  />
                )}

                {selectedEmployee && selectedModule === "contribution" && (
                  <FeedbackView
                    viewUserId={selectedEmployee.id}
                  />
                )}
              </div>

            </div>
          </div>
        </div>  
        </div>
      </div>
    </div>
  );
}

export default EmployeeList;
